import { Link } from "react-router-dom";
import { ArrowRight, Camera, Palette, Scissors, PawPrint } from "lucide-react";
import Header from "@/components/Header";
import { GROOMING_STYLES } from "@/lib/groomingStyles";
import PricingSection from "@/components/PricingSection";
import heroImage from "@/assets/hero-app-cover.jpg";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#fdfbf7]" data-testid="landing-page">
      <Header />

      {/* HERO */}
      <section className="paw-bg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
          <div className="md:col-span-6 fade-up order-2 md:order-1">
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-[#eae4d9] text-[#8a6d3b] text-[11px] font-semibold tracking-widest uppercase px-3.5 py-1.5 shadow-sm"
            >
              <PawPrint className="w-3.5 h-3.5 text-[#c08a3e]" /> Premium grooming previews
            </span>
            <h1
              data-testid="hero-headline"
              className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1a1712] leading-[1.05]"
            >
              See your dog's new look before the groomer does
            </h1>
            <p
              data-testid="hero-subheadline"
              className="mt-5 text-base sm:text-lg text-[#5b5242] leading-relaxed max-w-xl"
            >
              Upload a photo, choose a grooming style, and see a realistic preview of your dog before the clippers come out.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/studio"
                data-testid="hero-try-free-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#1a1712] hover:bg-[#2c261d] text-white font-semibold px-7 py-4 shadow-[0_14px_34px_-10px_rgba(26,23,18,0.55)] hover:shadow-[0_18px_40px_-10px_rgba(26,23,18,0.6)] transition-all active:scale-[0.98]"
              >
                Preview My Dog <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#how-it-works"
                data-testid="hero-how-it-works-btn"
                className="text-sm font-medium text-[#5b5242] hover:text-[#1a1712] underline underline-offset-4 decoration-[#d8cdb8] transition-colors"
              >
                See how it works
              </a>
            </div>
            <p
              data-testid="hero-support-text"
              className="mt-5 text-sm text-[#6b6252]"
            >
              Free to try • No credit card required
            </p>
          </div>

          <div className="md:col-span-6 fade-up delay-2 order-1 md:order-2">
            <div className="relative mx-auto max-w-md md:max-w-none">
              <div className="pointer-events-none absolute inset-6 -z-10 rounded-[2.5rem] bg-[#efe9dd] blur-2xl opacity-70" />
              <img
                src={heroImage}
                alt="A hand holding a phone showing the GroomingPreview.ai app with a before-and-after grooming preview of a small white dog"
                data-testid="hero-image"
                loading="eager"
                className="w-full h-auto max-h-[560px] object-contain mx-auto rounded-[2rem] shadow-[0_30px_70px_-24px_rgba(26,23,18,0.35)] select-none"
              />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        id="how-it-works"
        data-testid="how-it-works-section"
        className="py-20 bg-white border-y border-[#eae4d9]"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-bold tracking-widest uppercase text-[#c84125]">
              How it works
            </span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold text-[#0f172a] tracking-tight">
              Three steps between you and a better groomer visit.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                icon: Camera,
                title: "Upload your dog",
                copy: "Take or upload a clear photo of your dog. Face and body visible works best.",
              },
              {
                icon: Palette,
                title: "Choose a style",
                copy: "Explore grooming styles or describe your own custom look.",
              },
              {
                icon: Scissors,
                title: "Show your groomer",
                copy: "Save or share your personalized grooming preview card.",
              },
            ].map((s, i) => (
              <div
                key={s.title}
                data-testid={`how-step-${i + 1}`}
                className="bg-[#fdfbf7] rounded-2xl border border-[#eae4d9] p-6 hover:-translate-y-0.5 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#0f172a] text-white flex items-center justify-center">
                    <s.icon className="w-5 h-5" />
                  </span>
                  <span className="text-xs font-bold tracking-widest text-[#c84125]">
                    STEP {i + 1}
                  </span>
                </div>
                <h3 className="mt-4 text-lg font-semibold text-[#0f172a]">{s.title}</h3>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{s.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STYLE GALLERY */}
      <section id="styles" data-testid="styles-preview-section" className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="max-w-xl">
              <span className="text-xs font-bold tracking-widest uppercase text-[#c84125]">
                Styles
              </span>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold text-[#0f172a] tracking-tight">
                From teddy bear to sporty — say exactly what you want.
              </h2>
            </div>
            <Link
              to="/studio"
              data-testid="styles-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#0f172a] hover:bg-[#1e293b] text-white text-sm font-semibold px-5 py-3 transition"
            >
              Start your preview <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {GROOMING_STYLES.slice(0, 8).map((s) => (
              <div
                key={s.id}
                data-testid={`landing-style-card-${s.id}`}
                className="group rounded-2xl overflow-hidden border border-[#eae4d9] bg-white shadow-sm hover:shadow-md transition-all"
              >
                <div className="aspect-square overflow-hidden bg-[#f3efe6]">
                  <img
                    src={s.image}
                    alt={s.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-3">
                  <div className="text-sm font-semibold text-[#0f172a]">{s.name}</div>
                  <div className="text-xs text-[#64748b] mt-0.5 line-clamp-2">{s.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <PricingSection />

      {/* CTA STRIP */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grain rounded-3xl bg-[#0f172a] text-white p-8 sm:p-12 md:p-16 relative overflow-hidden">
            <div className="relative z-10 max-w-2xl">
              <span className="text-xs font-bold tracking-widest uppercase text-[#ff9d84]">
                Free · No signup · 2 free previews
              </span>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight">
                Walk into your next appointment with a picture, not a paragraph.
              </h2>
              <p className="mt-4 text-[#cbd5e1] max-w-xl">
                A downloadable grooming card that shows your groomer exactly what you're picturing.
              </p>
              <div className="mt-8">
                <Link
                  to="/studio"
                  data-testid="cta-strip-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#ff6b4a] hover:bg-[#e85535] text-white font-semibold px-6 py-3.5 shadow-lg shadow-[#ff6b4a]/25 transition-all active:scale-[0.98]"
                >
                  Try It Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="pointer-events-none absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-[#ff6b4a]/25 blur-3xl" />
            <div className="pointer-events-none absolute -left-24 -top-24 w-72 h-72 rounded-full bg-[#c84125]/20 blur-3xl" />
          </div>
        </div>
      </section>

      <footer className="border-t border-[#eae4d9] bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[#0f172a] font-display font-bold">
            <PawPrint className="w-4 h-4 text-[#ff6b4a]" /> GroomingPreview.ai
          </div>
          <p className="text-xs text-[#64748b] max-w-lg text-center sm:text-right">
            Previews are visual references only. Final grooming results depend on your dog's coat, breed and professional groomer assessment.
          </p>
        </div>
      </footer>
    </div>
  );
}
